import { AI_BUILDER_MENTION_ID, AI_BUILDER_MENTION_TYPE, mentionMarkup } from '@shared/task-contexts';
import { FACTORY_STEER_LANG } from '@shared/factory-progress';
import { TASK_AI_BUILDER_NAME, type TaskEvent } from '@shared/types/tasks';

/**
 * T-44: a steering option from the coding agent's progress report, sent as the comment a person would
 * write: an @AI builder mention, then the option. The comment goes into the run's turn (T-41).
 */
export function steerComment(option: string): string {
  return `${mentionMarkup(TASK_AI_BUILDER_NAME, AI_BUILDER_MENTION_TYPE, AI_BUILDER_MENTION_ID)} ${option}`;
}

const AI_PREFIX = mentionMarkup(TASK_AI_BUILDER_NAME, AI_BUILDER_MENTION_TYPE, AI_BUILDER_MENTION_ID);

/** Does this event carry steering options (a factory message with a factory-steer block)? */
export const hasSteerOptions = (event: TaskEvent) => Boolean(event.actor_label && event.body?.includes(FACTORY_STEER_LANG));

/** The options people sent after the event at `index`: the text of each later @AI builder comment by a person. */
export function steersSentAfter(events: TaskEvent[], index: number): string[] {
  const sent: string[] = [];
  for (const e of events.slice(index + 1)) {
    if (e.kind !== 'chat' || e.actor_label || !e.body?.startsWith(AI_PREFIX)) continue;
    sent.push(e.body.slice(AI_PREFIX.length).trim());
  }
  return sent;
}

/** T-41: the run whose working agent this comment went into (its "Sent to R-N" tag); null when it didn't. */
export function steeredRunNumber(event: TaskEvent): number | null {
  if (event.kind !== 'chat' || event.actor_label) return null;
  const n = event.payload?.steered_run_number;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : null;
}

/** T-41: the run holding this comment until its agent works (its "Waiting for R-N's agent" tag); null when it isn't held. */
export function heldRunNumber(event: TaskEvent): number | null {
  if (event.kind !== 'chat' || event.actor_label || steeredRunNumber(event) !== null) return null;
  const n = event.payload?.held_run_number;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : null;
}
